import axios from 'axios'
import jwtDecode from 'jwt-decode'
import { inc as semverInc } from 'semver'
//import fs from 'fs'
import _ from 'lodash'

export function apiFactory({
  protocol = 'https://',
  servicesPrefix = 'services',
  domain = 'formhero.cloud',
  token = null,
} = {}) {
  const getFormHeroServicesUrl = () => `${protocol}${servicesPrefix}.${domain}`
  const formheroServices = async (path, config = {}, catchErrors = true) => {
    const axiosConfig = {
      url: `${getFormHeroServicesUrl()}/${path}`,
      headers: {
        'Content-Type': 'application/json'
      },
      ...config,
    }

    if(token){
      axiosConfig.headers["X-Formhero-Token"] = token
    }

    const promise = axios(axiosConfig).then((r) => r.data)

    if (catchErrors) {
      promise.catch((error) => {
        console.error(`Error on access to ${axiosConfig.url}: ${error}`)
      })
    }

    return promise
  }

  // ==================
  // Org design
  // ==================
  const getOrgsList = async () => formheroServices(`auth/session/organizations`).then(r => r.value)
  const getTeamsList = async (o) => formheroServices(`auth/session/organization/${o}/teams`).then(r => r.value)

  // ==================
  // SmartForms
  // ==================

  const getPublishedForm = async (o, t, s) => {
    // Original definition of this function was only:
    // -----
    // return formheroServices(`library/formflow/${o}/${t}/${s}`)
    // -----
    // The `library/formflow/${o}/${t}/${s}` endpoint will fail with a 403 on forms that require signed requests,
    // this encouraged us to move away from using this endpoint in our API wrapper.

    if (!token) {
      return formheroServices(`library/formflow/${o}/${t}/${s}`).then(r => r.value)
    }

    const info = await getPublishedVersionInfo(o, t, s)
    return formheroServices(`library/smartforms/${info.smartForm}`).then(
      (response) => response.value
    )
  }

  /**
   * GETs information on the latest published version of a SmartForm
   *
   * @function getPublishedVersionInfo
   *
   *
   * @param {string} organization
   * @param {string} team
   * @param {string} slug - The slug for the SmartForm
   * 
   * @example
    {
      "authentication": {
          "type": "none",
          "userPathReport": true,
          "suppressApiAudit": false
      },
      "increment": "patch",
      "commitMessage": "Update to v2.11.40",
      "label": "3.2.2",
      "author": {
          "cuid": "ck93belw201uu0iud48tqja7s",
          "emailAddress": "danella.olsen@daylight.io"
      },
      "created": "2023-03-17T20:31:52.741Z",
      "smartForm": "clfczx8sw00k13fqkk4c5vwda",
      "publishedOn": "2023-03-17T20:31:54.255Z"
  }
   * getPublishedVersionInfo("some-org", "my-team", "my-super-cool-form")
   * @return {Promise<object>} An object with the info about the latest published version of the form
   */
  const getPublishedVersionInfo = async (o, t, s) => {
    // Extracting this from the list of SmartForms is not ideal, however, the other option is using the
    // `library/formflow/${o}/${t}/${s}` endpoint. Unfortunately, this endpoint does not work in all cases
    // (it returns 403s for forms where signed requests are required)

    const forms = await getFormsList(o, t)

    const form = forms.find((f) => f.slug === s)

    if (_.isNil(form)) return null

    return form.publishedVersion
  }

  const getPublishedCuid = async (o, t, s) =>
    getPublishedVersionInfo(o, t, s).then((form) => form.smartForm)

  const getRawFormDataWithDocuments = async (cuid) =>
    formheroServices(`library/smartforms/${cuid}?edit=true`).then(
      (r) => r.value
    )

  const patchFormVersion = (cuid, data) => {
    return formheroServices(`library/smartforms/${cuid}`, {
      method: 'PATCH',
      data,
    })
  }
  const publishFormVersion = (cuid) => {
    return patchFormVersion(cuid, { publishStatus: 'PUBLISHED' })
  }
  const createFormVersion = async (
    o,
    t,
    s,
    form,
    commitMsg,
    { patchLevel = 'minor', publish = false } = {}
  ) => {
    const {
      cuid: publishedCuid,
      title: publishedTitle,
    } = await getPublishedForm(o, t, s)

    const allVersions = await getFormVersions(publishedCuid)
    const latestVersion = allVersions[allVersions.length - 1].label

    const version = {
      label: semverInc(latestVersion, patchLevel),
      increment: patchLevel,
      commitMessage: commitMsg,
      author: {
        cuid: jwtDecode(token).userCuid,
        emailAddress: jwtDecode(token).emailAddress,
      },
    }

    Object.assign(form, {
      organization: o,
      team: t,
      slug: s,
      versionCuid: publishedCuid,
      publishStatus: 'DRAFT',
      version,
      title: publishedTitle,
    })

    console.log('Preparing to submit')

    console.log('Logging submit body to submit.json')
    const payload = { formflow: form }
    fs.writeFileSync('submit.json', JSON.stringify(payload, null, 2))
    console.log(JSON.stringify(form.version, null, 2))

    return formheroServices(`library/formflow/${o}/${t}/${s}`, {
      method: 'POST',
      data: payload,
    })
      .then(({ result, cuid, ...rest }) => {
        if (result !== 'success') {
          throw new Error('Failure in new form creation', rest)
        }

        if (publish) {
          return publishFormVersion(cuid)
        }

        return cuid
      })
      .catch((e) => console.error(e.response.data))
  }

  const getFormVersions = async (cuid) =>
    formheroServices(`library/smartforms/${cuid}/versions`)

  const getFormsList = async (organization, team = null) =>
    formheroServices(
      team ?
        `library/formflows/${organization}/${team}/list` :
        `library/formflows/${organization}/list`
    ).then(result => result.value)

  // ==================
  // PickLists
  // ==================

  const getPickLists = async (o, t) =>
    formheroServices(`library/pickLists/${o}/${t}/list`).then((r) => r.value)
  const getPickList = async (o, t, s) =>
    formheroServices(`library/pickList/${o}/${t}/${s}`, {}, false).then((r) => r.value)
  const createPickList = async (o, t, s, { title, list }) => {
    console.log(`createPickList called`)
    console.log(`Array.isArray(list) = ${Array.isArray(list)}`)
    console.log(`Title=${title}. List=${JSON.stringify(list, null, 2)}`)
    return formheroServices(`library/pickLists/${o}/${t}/${s}`, {
      method: 'post',
      data: { title, list: JSON.stringify(list) },
    })
      .then((v) => v.value)
      .then((allPickListsForTeam) =>
        allPickListsForTeam.find((pl) => (pl.titleSlug = s))
      )
  }
  // create endpoint actually has upsert semantics.
  // (i.e. can be called multiple times, and forms that reference the picklist
  //  automatically see the update)
  const upsertPickList = createPickList

  // ==================
  // ApiIntegrations
  // ==================

  // integrations are distinct from Api Integrations as the former is webhooks (for submit)
  // the latter is "Custom Api Integrations" (for pulling/pushing data during form)
  const getIntegrations = async () =>
    formheroServices(`library/integrations/list`).then(
      (r) => r /* yup, this endpoint is different; no value */
    )

  const getIntegrationsForTeam = async (organization, team) =>
    formheroServices(
      `library/integration/authorized/${organization}/${team}`
    ).then((r) => r /* yup, this endpoint is different; no value */)

  const getApiIntegrations = async () =>
    formheroServices(`library/api-integration/list`).then(
      (r) => r /* yup, this endpoint is different; no value */
    )

  const createApiIntegration = async (data) =>
    formheroServices(`library/api-integration`, {
      method: 'post',
      data,
    }).then((result) => result)

  const updateApiIntegration = async (cuid, data) =>
    formheroServices(`library/api-integration/${cuid}`, {
      method: 'put',
      data,
    }).then((result) => result)

  // ==================
  // PdfTemplates
  // ==================

  const getPdfTemplate = async (organization, team, slug) =>
    formheroServices(
      `library/pdf-templates/${organization}/${team}/${slug}`
    ).then((r) => r.value)

  const getPdfTemplates = async (organization, team) =>
    formheroServices(`library/pdf-templates/${organization}/${team}/list`).then(
      (r) => r.value
    )

  /**
   * GETs document details from library/pdf-template-versions/${templateCuid}/${version}/details
   *
   * @function getPdfTemplateVersionDetails
   *
   *
   * @param {string} templateCuid - The cuid associated with a specific pdfTemplate
   * @param {string} version      - The version number associated with the pdfTemplateVersion
   *
   * @example
   * // returns {
   *   "usedBy": [],
   *   "_id": "6169075802818000b2a25069",
   *   "versionTimestamp": "2021-10-15T04-45-12.015Z",
   *   "resourceDescriptor": "ckurw0jpt00044yneg15yapmn",
   *   "created": "2021-10-15T04:45:12.015Z",
   *   "resourcePath": "some/path/original.pdf",
   *   "md5Checksum": "62b115efc54a616f592ab7a4c7624ee0",
   *   "cuid": "some-cuid",
   *   "fieldNameMappings": [],
   *   "__v": 0
   * }
   * getPdfTemplateVersionDetails("some-cuid","2021-10-15T04-45-12.015Z")
   * @return {Promise<object>} An object with the details of the specified pdfTemplateVersion
   */

  const getPdfTemplateVersionDetails = async (templateCuid, version) =>
    formheroServices(
      `library/pdf-template-versions/${templateCuid}/${version}/details`
    ).then((r) => r.value)

  /**
   * A function that retrieves the PDF binary for a specific template version
   *
   * @function getPdfTemplateVersionPdf
   *
   * @async Delete this line if not async
   *
   * @param {string} organization
   * @param {string} team
   * @param {string} templateSlug - The slug for the pdf template
   * @param {string} version -  The version timestamp associated with the pdfTemplateVersion
   *
   * @example <caption>Example Call</caption>
   * getPdfTemplateVersionPdf("ProofGov", "default", "some-cool-form", '2021-10-15T04:45:12.015Z')
   *
   * @return {Promise<arraybuffer>} The pdf binary
   */

  const getPdfTemplateVersionPdf = async (
    organization,
    team,
    templateSlug,
    version
  ) =>
    formheroServices(
      `library/pdf-templates/${organization}/${team}/${templateSlug}/${version}/pdf`,
      {
        headers: {
          'Content-Type': 'application/pdf',
          'X-Formhero-Token': token,
        },
        // Thanks be to this article: https://www.linkedin.com/pulse/converting-pdf-binary-base64-nodejs-trick-end-andrew-ribeiro/
        responseType: 'arraybuffer',
        responseEncoding: 'binary',
      }
    )

  /**
   * POSTs to the library/pdf-template-versions/${templateCuid}/${version}/saveMappings
   * endpoint to update the list of fieldNameMappings
   *
   * @function updatePdfTemplateVersionMappings
   *
   * @param {string}        templateCuid - The cuid associated with a specific pdfTemplate
   * @param {string}        version      - The version number associated with the pdfTemplateVersion
   * @param {Array<object>} mappings     - a list of field fieldNameMappings to be applied to the targeted version
   *
   * @example
   * // returns
   * {
   *   usedBy: [],
   *   _id: '6169075802818000b2a25069',
   *   versionTimestamp: '2021-10-15T04-45-12.015Z',
   *   resourceDescriptor: 'ckurw0jpt00044yneg15yapmn',
   *   created: '2021-10-15T04:45:12.015Z',
   *   resourcePath: 'the/path/to/resource',
   *   md5Checksum: '62b115efc54a616f592ab7a4c7624ee0',
   *   cuid: 'some-template-cuid',
   *   fieldNameMappings: [
   *     {
   *       hideFromFormflowEditor: false,
   *       _id: '1234',
   *       pdfFieldKey: 'Some Field',
   *       fhFieldKey: 'q1',
   *     },
   *   ],
   *   __v: 0,
   * }
   *
   * updatePdfTemplateVersionMappings(
   *   'some-template-cuid',
   *   '2021-10-15T04-45-12.015Z',
   *   [
   *     {
   *       hideFromFormflowEditor: false,
   *       _id: '1234',
   *       pdfFieldKey: 'Some Field',
   *       fhFieldKey: 'q1',
   *     },
   *   ]
   * )
   * @return {Promise<object>} The updated pdfTemplateVersion
   */

  const updatePdfTemplateVersionMappings = async (
    templateCuid,
    version,
    mappings
  ) =>
    formheroServices(
      `library/pdf-template-versions/${templateCuid}/${version}/saveMappings`,
      { method: 'post', data: { mappings } }
    ).then((r) => r /* yup, this endpoint is different; no value */)

  /**
   * Creates a new pdfTemplateVersion using the library/pdf-templates/${organization}/${team}/${templateSlug}
   * POST endpoint
   *
   * @function upsertPdfTemplateVersion
   *
   * @param {string} organization
   * @param {string} team
   * @param {string} templateSlug            - The slug for the pdfTemplate
   * @param {object} body                    - Contains pdf and destination information (destionation is inferred if not prvided)
   * @param {object} body.base64Pdf          - Contains the pdf and all its details
   * @param {string} body.base64Pdf.base64   - The PDF document encoded as base64
   * @param {number} body.base64Pdf.filesize - Size of the file in bytes
   * @param {string} body.base64Pdf.filetype - MIME type of the file. Eg: 'application/pdf'
   * @param {string} body.base64Pdf.filename - Filename with extension. Eg: my-document.pdf
   * @param {string} body.title              - The title of the PdfTemplate; only required if the PdfTemplate does not already exist
   *
   * @example
   * // returns {
   *   descriptorCuid: 'destination-cuid',
   *   versionTimestamp: 'the-timestamp-for-destination-version',
   *   fileFolder: `pdf-templates/my-org/my-team/my-slug/the-timestamp-for-version`,
   * }
   *
   * upsertPdfTemplateVersion('my-org', 'my-team', 'my-slug', {
   *   base64Pdf: {
   *     base64: '<base-64-pdf>',
   *     filesize: 3213,
   *     filetype: 'application/pdf',
   *     filename: 'my-document.pdf',
   *   },
   * })
   *
   * @return {Promise<object>} Contains data (fileFolder, version etc.) on the newly created pdfTemplateVersion
   */

  // You never create a PdfTemplate on its own, the creation of the first version will create the template
  const upsertPdfTemplateVersion = async (
    organization,
    team,
    templateSlug,
    body
  ) =>
    formheroServices(
      `library/pdf-templates/${organization}/${team}/${templateSlug}`,
      {
        method: 'post',
        data: {
          organization: organization,
          workgroup: team,
          titleSlug: templateSlug,
          ...body,
        },
      },
      // Because the error result is potentially useful (eg. a 409 pointing to a duplicate templateVersion),
      // we do not want to catch this error, instead, we let the user of this function manage the result
      false
    ).then((r) => {
      console.log('Result from API', JSON.stringify(r))

      // This if block is here for a gross reason...
      //
      // There are two types of errors that can return from the API.
      // 1. System constraint "errors", eg. 'pdf-template.create.alreadyExists'
      // 2. Unexpected server errors. Example: {"action":"pdf-template.create","result":"failure","cause":"socket hang up"}
      //
      // The first case comes in with a 4xx status code and Axios will throw an error; the second case IS treated as
      // a success (with a resopnse code of 200), and will make it into the `then`, even though the request has failed
      //
      // This conditional catches these false positive success responses and returns a Promise.reject whose error mimics key properties
      // of an Axios response.
      //

      if (r.result === 'failure') {
        console.log('Received a false positive. Throwing result as error.')
        return Promise.reject({
          response: { data: r },
          status: 500,
          message: `False positive response from FormHero API: ${r.cause}`,
        })
      }

      return r.value
    })

  const submit = (formCuid, submissionNodeUuid, data) => {
    return formheroServices(
      `submissions/step/execute`,
      {
        method: 'post',
        data: {
          smartFormCuid: formCuid,
          currentNode: submissionNodeUuid,
          data
        },
      }
    ).then(result => {
      // add urls to the artifacts for easier consumption downstream

      function buildUrlForArtifact(artifact) {
        let [ artifactType, sessionId, artifactUuid, fileName ] = artifact.id.split('::')
        return [
          `${getFormHeroServicesUrl()}/submissions/session-resource`,
          artifactType,
          sessionId,
          artifactUuid,
          encodeURIComponent(fileName)
        ].join('/')
      }

      result.artifactManifest.artifacts.forEach(a => a.url = buildUrlForArtifact(a))

      return result
    })
  }

  class DataSetInterface {
    constructor(slug) {
      this.slug = slug
    }

    create() { throw new Error("Create Not Implemented") }

    search(secret, searchValues=[]) {
      return formheroServices(
        `data-sets/data/${this.slug}/getData`,
        {
          method: 'POST',
          data: {
            secret,
            values: searchValues
          }
        }
      ).then(r => {
        if(r.data === undefined) {
          throw new Error(`No results for secret ${secret}, searchValues ${searchValues} on ${this.slug}`)
        }

        return r.data
      })
    }

    upsert(secret, record) {
      return formheroServices(
        `data-sets/upload/${this.slug}/webhook-post/upload`,
        {
          method: 'POST',
          data: {
            type: 'formDataSet',
            data: record,
            secret,
          }
        }
      ).then((response) => response.secret)
    }
  }

  return {
    token,
    getOrgsList,
    getTeamsList,
    createApiIntegration,
    createFormVersion,
    createPickList,
    upsertPickList,
    domain,
    formheroServices,
    getApiIntegrations,
    getFormsList,
    getFormVersions,
    getIntegrations,
    getIntegrationsForTeam,
    getPdfTemplate,
    getPdfTemplates,
    getPdfTemplateVersionDetails,
    getPdfTemplateVersionPdf,
    getPickList,
    getPickLists,
    getPublishedCuid,
    getPublishedForm,
    getPublishedVersionInfo,
    getRawFormDataWithDocuments,
    updateApiIntegration,
    updatePdfTemplateVersionMappings,
    upsertPdfTemplateVersion,
    submit,
    DataSetInterface
  }
}
